import type { Question } from '../types';
import { apiFetch } from '../../../services/api';

export interface SavedQuestion {
  id: string;
  questionId: string;
  questionText: string;
  correctAnswer: string;
  userAnswer?: string;
  sdgNumbers: number[];
  starredAt: number;
}

export const SAVED_QUESTIONS_ROUTES = {
  list: '/api/game/saved-questions/',
  create: '/api/game/saved-questions/',
  remove: (savedQuestionId: string) => `/api/game/saved-questions/${savedQuestionId}/`,
};

type Store = Record<string, SavedQuestion[]>;

const store: Store = {};
const listeners = new Set<() => void>();
const EMPTY_LIST: SavedQuestion[] = [];

function getEffectiveUserId(userId: number | null): number | null {
  return userId;
}

function emitChange() {
  listeners.forEach((listener) => listener());
}

function setStoreForUser(userId: number, questions: SavedQuestion[]) {
  store[String(userId)] = questions;
  emitChange();
}

export function subscribeSavedQuestions(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSavedQuestionsSnapshot(userId: number | null): SavedQuestion[] {
  const effectiveUserId = getEffectiveUserId(userId);
  if (!effectiveUserId) return EMPTY_LIST;
  return store[String(effectiveUserId)] ?? EMPTY_LIST;
}

export async function listSavedQuestions(userId: number | null): Promise<SavedQuestion[]> {
  const effectiveUserId = getEffectiveUserId(userId);
  if (!effectiveUserId) return [];

  const response = await apiFetch(SAVED_QUESTIONS_ROUTES.list, { method: 'GET' });
  if (!response.ok) {
    throw new Error(`Failed to list saved questions: ${response.status}`);
  }
  const data = (await response.json()) as SavedQuestion[];
  setStoreForUser(effectiveUserId, data);
  return data;
}

export async function saveQuestion(
  userId: number | null,
  question: Question,
  userAnswer?: string,
): Promise<SavedQuestion | null> {
  const effectiveUserId = getEffectiveUserId(userId);
  if (!effectiveUserId) return null;

  const correct = question.options.find((option) => option.isCorrect);
  if (!correct) return null;

  const key = String(effectiveUserId);
  const existing = store[key] ?? [];

  const response = await apiFetch(
    SAVED_QUESTIONS_ROUTES.create,
    {
      method: 'POST',
      body: JSON.stringify({
        question_id: question.questionId,
        question_text: question.dialogueText,
        correct_answer: correct.text,
        user_answer: userAnswer ?? '',
        sdgNumbers: question.sdgNumbers ?? [],
      }),
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to save question: ${response.status}`);
  }
  const saved = (await response.json()) as SavedQuestion;
  const withoutSameQuestion = existing.filter((item) => item.questionId !== saved.questionId);
  store[key] = [saved, ...withoutSameQuestion];
  emitChange();
  return saved;
}

export async function deleteSavedQuestion(userId: number | null, savedQuestionId: string): Promise<void> {
  const effectiveUserId = getEffectiveUserId(userId);
  if (!effectiveUserId) return;
  const key = String(effectiveUserId);

  const response = await apiFetch(SAVED_QUESTIONS_ROUTES.remove(savedQuestionId), { method: 'DELETE' });
  if (!response.ok && response.status !== 204) {
    throw new Error(`Failed to delete saved question: ${response.status}`);
  }
  store[key] = (store[key] ?? []).filter((item) => item.id !== savedQuestionId);
  emitChange();
}

